import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { getAccountIsRBL } from '@helpers/Accounts/getAccountIsRBL';
import { getAccountIsRCF } from '@helpers/Accounts/getAccountIsRCF';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { getTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus';
import { TTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus.types';
import { fetchGetAccounts, fetchGetApplication } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';

type TFAQData = {
    loanDuration: number;
    topUpStatus: TTopUpStatus;
    isOverdue: boolean;
    productType: TAccount['product_type'];
    isRBL: boolean;
    isInFirstRepaymentTopUpSplitTest: boolean;
    isRCF: boolean;
};

export const useGetFAQData = (
    accountId: NonNullable<TAccount['account_id']>
): TFAQData => {
    const stateKey = useStateKey();

    const { data: account } = useQuery({
        queryKey: ['accounts', stateKey],
        queryFn: () => fetchGetAccounts({ stateKey }),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true
    });

    const { data: applicationResponse } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            }),
        suspense: true
    });

    const topUpFirstRepaymentSplitTestBranch = useSplitTestBranch({
        test: 'repeats_new_topup_eligibility_criteria_2025'
    });
    const isInFirstRepaymentTopUpSplitTest =
        topUpFirstRepaymentSplitTestBranch === 'INCLUDE';

    if (!account || !applicationResponse) {
        throw new Error();
    }
    const topUpStatus = getTopUpStatus(account, applicationResponse.data);

    return {
        loanDuration: getDurationInMonths(account.duration!),
        topUpStatus,
        isOverdue: Boolean(account.loan_status === 'overdue'),
        productType: account.product_type,
        isRBL: getAccountIsRBL(account),
        isInFirstRepaymentTopUpSplitTest,
        isRCF: getAccountIsRCF(account)
    };
};
